import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWeeklyFolders } from '@/contexts/WeeklyFolderContext';
import styles from './Layout.module.css';

const navItems: Array<{ path: string; label: string; icon: string; adminOnly?: boolean }> = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/documents', label: 'Documents', icon: '📄' },
  { path: '/commentary', label: 'Commentary', icon: '✍️' },
  { path: '/templates', label: 'Templates', icon: '📝' },
  { path: '/feedback', label: 'Feedback', icon: '💬' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
  { path: '/users', label: 'Users', icon: '👥', adminOnly: true },
];

export function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { currentFolder, selectedFolder, selectFolder, folders } = useWeeklyFolders();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const activeFolders = folders.filter((f) => f.status === 'active');

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <img src="/cap8-logo-mark-full-color-rgb.png" alt="Logo" className={styles.logoImage} />
          <h1>Market Commentary</h1>
        </div>

        {/* Folder Selector */}
        <div className={styles.folderSelector}>
          <label className={styles.folderLabel}>Active Folder</label>
          <select
            className={styles.folderSelect}
            value={selectedFolder?.id || ''}
            onChange={(e) => {
              const folder = folders.find((f) => f.id === e.target.value);
              selectFolder(folder || null);
            }}
          >
            {activeFolders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.title}
                {folder.id === currentFolder?.id ? ' (Current)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          {navItems
            .filter((item) => !item.adminOnly || isAdmin)
            .map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                }
                end={item.path === '/'}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
        </nav>

        {/* User Info */}
        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <div className={styles.userName}>{user?.name}</div>
            <div className={styles.userRole}>{user?.role}</div>
          </div>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
